import * as React from 'react';
import {Query} from 'react-apollo';
import {DragDropContext} from 'react-beautiful-dnd';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {
    safelyGetGraphQLErrorMessage,
    DEFAULT_GRAPHQL_ERROR_MESSAGE,
} from 'nutshell-core/utils/graphql-errors';

import type {
    EmailAutomation as EmailAutomationFragment,
    SalesAutomationStage as SalesAutomationStageFragment,
    EmailsForStagesetQuery as EmailsForStagesetQueryQuery,
    EmailsForStagesetQueryVariables as EmailsForStagesetQueryQueryVariables,
} from 'nutshell-graphql-types';

import {NUTSHELL_EMAIL_MARKETING} from '../../constants';
import {NutshellEmailMarketingCtaBanner} from '../../ctas/nutshell-email-marketing-cta-banner';
import {useGetSalesAutomationableEmailTemplates} from '../../graphql/hooks/use-get-sales-automationable-email-templates';
import GET_EMAIL_AUTOMATIONS from '../../graphql/queries/get-email-automations-for-stageset.graphql';

import {calculateBoardWidth} from '../calculate-board-width';
import {StageCardsEmailsContainer} from '../card-containers/stage-cards-emails-container';

import {CollapsibleSection} from './collapsible-section';
import {
    translateStageSubscriptionsForBoard,
    findDuplicatePositionStages,
} from './translate-stage-subscriptions-for-board';

type Props = OwnProps & {
    isLoadingEmailAutomations: boolean;
    hasAutomations: boolean;
    emailAutomations:
        | {
              [key: string]: EmailAutomationFragment[];
          }
        | {
              [id: string]: [];
          };
    // @ts-expect-error
    errorMessage: string | React.ReactElement<React.ComponentProps<string>> | null | undefined;
};

type State = {
    isCardBeingDragged: boolean;
};

/*
 * Top-level component responsible for rendering the entire "emails"
 * section (row) on the sales automation setup board.
 */
export class SalesAutomationNutshellEmailMarketingComponent extends React.PureComponent<
    Props,
    State
> {
    constructor() {
        // @ts-expect-error
        super();

        this.state = {
            isCardBeingDragged: false,
        };
    }

    render() {
        const boardWidth = calculateBoardWidth(this.props.stages.length);

        const collapsibleSectionContents = this.props.hasAutomations ? (
            this.props.stages.map((stage) => {
                const subscriptionsForStage = this.props.emailAutomations[stage.id];

                return (
                    <StageCardsEmailsContainer
                        type={NUTSHELL_EMAIL_MARKETING}
                        key={stage.id}
                        parentId={stage.id}
                        errorMessage={this.props.errorMessage}
                        isLoading={this.props.isLoadingEmailAutomations}
                        hideCards={this.props.hideCards}
                        // @ts-expect-error
                        emailAutomations={subscriptionsForStage}
                        isCardBeingDragged={this.state.isCardBeingDragged}
                    />
                );
            })
        ) : (
            <NutshellEmailMarketingCtaBanner width={boardWidth} />
        );

        return (
            <DragDropContext
                onDragStart={() => this.setState({isCardBeingDragged: true})}
                onDragEnd={this.handleItemMoved}
            >
                <CollapsibleSection type={NUTSHELL_EMAIL_MARKETING}>
                    {!this.props.isLoadingEmailAutomations && collapsibleSectionContents}
                </CollapsibleSection>
            </DragDropContext>
        );
    }

    // TODO: implement email dragging
    handleItemMoved = () => {
        this.setState({isCardBeingDragged: false});
    };
}

type OwnProps = {
    stages: SalesAutomationStageFragment[];
    hideCards: boolean;
};
type RoutedProps = OwnProps & ContextRouter;

class StagesetQuery extends Query<
    EmailsForStagesetQueryQuery,
    EmailsForStagesetQueryQueryVariables
> {}

const Stageset = (props: RoutedProps) => {
    const {stagesetId} = props.match.params;

    const {isLoading: isLoadingTemplates, templates} =
        useGetSalesAutomationableEmailTemplates(stagesetId);

    if (!stagesetId) return null;

    return (
        <StagesetQuery
            query={GET_EMAIL_AUTOMATIONS}
            fetchPolicy='network-only'
            variables={{stagesetId}}
        >
            {({data, loading, error}) => {
                const stageset = data ? data.stageset : null;
                const accumulator: {
                    [id: string]: [];
                } = {};
                // Create a default set of empty mailchimp subscriptions, in case
                // we are unable to translate the subscriptions for the board.
                // @ts-expect-error
                let emailAutomations = props.stages.reduce((acc, stage) => {
                    acc[stage.id] = [];

                    return acc;
                }, accumulator);
                // @ts-expect-error
                let errorMessage = safelyGetGraphQLErrorMessage(error);

                // Only show nutshell email sequence lists
                const filteredEmailAutomations =
                    stageset &&
                    stageset.editableProcessTemplate &&
                    stageset.editableProcessTemplate.emailAutomations
                        ? stageset.editableProcessTemplate.emailAutomations.filter(
                              (subscription) => {
                                  return (
                                      subscription &&
                                      subscription.emailList &&
                                      subscription.emailList.__typename === 'EmailSequenceTemplate'
                                  );
                              }
                          )
                        : null;

                try {
                    emailAutomations = translateStageSubscriptionsForBoard(
                        filteredEmailAutomations,
                        props.stages
                    );
                } catch (e: any) {
                    if (e.message === 'Non-unique stage positions.') {
                        const dupeStages = findDuplicatePositionStages(props.stages);
                        const dupeStagesText = dupeStages
                            .map((stage) => `"${stage.name}"`)
                            .join(' and ');
                        const errorText = `This is weird, but rearranging stages ${dupeStagesText} should fix this error.`;
                        // @ts-expect-error
                        errorMessage = (
                            <span>
                                {errorText}
                                <br />
                                Or <a href='mailto:support@nutshell.com'>contact support</a>
                            </span>
                        );
                    } else {
                        errorMessage = DEFAULT_GRAPHQL_ERROR_MESSAGE;
                    }
                }

                return (
                    <SalesAutomationNutshellEmailMarketingComponent
                        stages={props.stages}
                        hideCards={props.hideCards}
                        isLoadingEmailAutomations={loading || isLoadingTemplates}
                        hasAutomations={templates.length > 0}
                        emailAutomations={emailAutomations}
                        errorMessage={errorMessage}
                    />
                );
            }}
        </StagesetQuery>
    );
};

// @ts-expect-error
export const SalesAutomationNutshellEmailMarketing = withRouter<OwnProps>(Stageset);
